import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useRounds } from '../hooks/useRounds';
import { useTeams } from '../hooks/useTeams';
import './MyPicksTab.css';

function formatKickoffEDT(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZoneName: 'short',
  });
}

export default function MyPicksTab({ leagueId, uid }) {
  const rounds = useRounds();
  const teams  = useTeams();

  const [matches, setMatches]     = useState([]);
  const [userPicks, setUserPicks] = useState({});

  useEffect(() => {
    return onSnapshot(collection(db, 'matches'), (snap) => {
      setMatches(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('matches subscription error:', err));
  }, []);

  useEffect(() => {
    if (!uid || !leagueId) return;
    const q = query(
      collection(db, 'picks'),
      where('leagueId', '==', leagueId),
      where('userId', '==', uid)
    );
    return onSnapshot(q, (snap) => {
      const data = {};
      snap.docs.forEach((d) => { data[d.data().matchId] = { id: d.id, ...d.data() }; });
      setUserPicks(data);
    }, (err) => console.error('picks subscription error:', err));
  }, [leagueId, uid]);

  const lockedRoundIds = useMemo(() => {
    return new Set(
      Object.values(rounds)
        .filter((r) => r.status === 'locked' || r.status === 'complete')
        .map((r) => r.id)
    );
  }, [rounds]);

  // Show: matches user has picked + all matches in locked rounds
  const displayMatches = useMemo(() => {
    return matches
      .filter((m) => userPicks[m.id] || lockedRoundIds.has(m.round))
      .sort((a, b) => (a.kickoffTime?.seconds ?? 0) - (b.kickoffTime?.seconds ?? 0));
  }, [matches, userPicks, lockedRoundIds]);

  const record = useMemo(() => {
    let correct = 0, wrong = 0, pending = 0, totalPoints = 0;
    for (const m of matches) {
      const pick = userPicks[m.id];
      if (!pick) continue;
      if (m.status === 'final') {
        if ((pick.pointsEarned ?? 0) > 0) correct++;
        else wrong++;
        totalPoints += pick.pointsEarned ?? 0;
      } else {
        pending++;
      }
    }
    return { correct, wrong, pending, totalPoints };
  }, [matches, userPicks]);

  if (!matches.length) return <div className="mpt-loading">Loading…</div>;

  const hasPicks = Object.keys(userPicks).length > 0;
  const hasLockedMatches = lockedRoundIds.size > 0;

  if (!hasPicks && !hasLockedMatches) {
    return (
      <div className="mpt">
        <div className="mpt-empty">
          No picks yet — head to the Group Stage tab to make your predictions.
        </div>
      </div>
    );
  }

  return (
    <div className="mpt">
      {/* Record bar */}
      <div className="mpt-record-bar">
        <div className="mpt-record-cell mpt-record-cell--correct">
          <span className="mpt-record-val">{record.correct}</span>
          <span className="mpt-record-label">Correct</span>
        </div>
        <div className="mpt-record-cell mpt-record-cell--wrong">
          <span className="mpt-record-val">{record.wrong}</span>
          <span className="mpt-record-label">Wrong</span>
        </div>
        <div className="mpt-record-cell mpt-record-cell--pending">
          <span className="mpt-record-val">{record.pending}</span>
          <span className="mpt-record-label">Pending</span>
        </div>
        <div className="mpt-record-cell mpt-record-cell--pts">
          <span className="mpt-record-val">{record.totalPoints}</span>
          <span className="mpt-record-label">Points</span>
        </div>
      </div>

      {/* Pick rows */}
      <div className="mpt-list">
        {displayMatches.map((match) => {
          const pick = userPicks[match.id] ?? null;
          const homeTeam = teams[match.homeTeamId];
          const awayTeam = teams[match.awayTeamId];
          const isFinal  = match.status === 'final';
          const pickedTeam = pick ? teams[pick.pickedTeamId] : null;

          let status = 'pending';
          if (!pick)        status = 'nopick';
          else if (isFinal) status = (pick.pointsEarned ?? 0) > 0 ? 'correct' : 'wrong';

          return (
            <div key={match.id} className={`mpt-row mpt-row--${status}`}>
              {/* Match info */}
              <div className="mpt-match">
                <span className="mpt-teams">
                  {homeTeam?.flagCode ? <span className={`fi fi-${homeTeam.flagCode}`} /> : null} {homeTeam?.name ?? '…'}
                  <span className="mpt-vs"> vs </span>
                  {awayTeam?.flagCode ? <span className={`fi fi-${awayTeam.flagCode}`} /> : null} {awayTeam?.name ?? '…'}
                </span>
                <span className="mpt-kickoff">
                  {isFinal
                    ? `${match.homeScore} – ${match.awayScore}`
                    : formatKickoffEDT(match.kickoffTime)}
                </span>
              </div>

              {/* Pick */}
              <div className="mpt-pick">
                {pick ? (
                  <>
                    <span className="mpt-pick-team">
                      {pickedTeam?.flagCode ? <span className={`fi fi-${pickedTeam.flagCode}`} /> : ''} {pickedTeam?.name ?? '…'}
                    </span>
                    {pick.isUnique && (
                      <span className="mpt-badge mpt-badge--unique">Unique</span>
                    )}
                  </>
                ) : (
                  <span className="mpt-no-pick">No pick made</span>
                )}
              </div>

              {/* Points + status badge */}
              <div className="mpt-result">
                {pick && isFinal && (
                  <span className="mpt-points">{pick.pointsEarned ?? 0} pts</span>
                )}
                <span className={`mpt-badge mpt-badge--${status}`}>
                  {status === 'correct' ? 'Correct'
                    : status === 'wrong'  ? 'Wrong'
                    : status === 'nopick' ? '—'
                    : 'Pending'}
                </span>
              </div>
            </div>
          );
        })}

        {displayMatches.length === 0 && (
          <div className="mpt-empty">
            No picks yet. Head to the Group Stage tab to make your picks.
          </div>
        )}
      </div>
    </div>
  );
}
